import { useApolloClient } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import {
  AutocompleteInput,
  Confirm,
  Form,
  ReferenceInput,
  ResourceContextProvider,
  Title,
  useDataProvider,
  useNotify,
} from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import { FeatureGrantScopeType } from '../../../graphql/types';
import useCanWrite from '../../../hooks/useCanWrite';
import { ResourceLink } from '../../reusable/ResourceLink';
import AutocompleteClientInput from '../components/AutocompleteClientInput';
import {
  analyzeFeatureGrants,
  CleanupVerdict,
  GrantAnalysis,
} from './analyzeFeatureGrants';
import { CleanupData, loadCleanupData } from './loadCleanupData';

const DELETE_CONCURRENCY = 10;

interface CleanupFilter {
  clientId: string;
  featureId?: string;
}

const SCOPE_RESOURCE: Partial<Record<FeatureGrantScopeType, string>> = {
  [FeatureGrantScopeType.User]: 'User',
  [FeatureGrantScopeType.Studio]: 'Studio',
  [FeatureGrantScopeType.Location]: 'CoachLocation',
  [FeatureGrantScopeType.Client]: 'Client',
};

const renderTarget = ({ grant, targetLabel }: GrantAnalysis) => {
  const resource = SCOPE_RESOURCE[grant.scopeType];
  return resource ? (
    <ResourceLink name={resource} id={grant.scopeId}>
      {targetLabel}
    </ResourceLink>
  ) : (
    targetLabel
  );
};

const GrantTable: React.FC<{
  analyses: GrantAnalysis[];
  selected: Set<string>;
  // Absent when the admin can't write: no checkboxes, just the analysis.
  onToggle?: (id: string) => void;
  onToggleAll?: (checked: boolean) => void;
}> = ({ analyses, selected, onToggle, onToggleAll }) => {
  const allSelected =
    analyses.length > 0 &&
    analyses.every((analysis) => selected.has(analysis.grant.id));
  const someSelected = analyses.some((analysis) =>
    selected.has(analysis.grant.id),
  );
  return (
    <TableContainer component={Paper} sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              {onToggle && onToggleAll && (
                <Checkbox
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={(event) => onToggleAll(event.target.checked)}
                  inputProps={{ 'aria-label': 'Select all' }}
                />
              )}
            </TableCell>
            <TableCell>Feature</TableCell>
            <TableCell>Scope</TableCell>
            <TableCell>Target</TableCell>
            <TableCell>State</TableCell>
            <TableCell>Resolves to without it</TableCell>
            <TableCell>Why</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {analyses.map((analysis) => {
            const { grant } = analysis;
            return (
              <TableRow key={grant.id} hover>
                <TableCell padding="checkbox">
                  {onToggle && (
                    <Checkbox
                      checked={selected.has(grant.id)}
                      onChange={() => onToggle(grant.id)}
                      inputProps={{ 'aria-label': `Select grant ${grant.id}` }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <ResourceLink name="Feature" id={grant.featureId}>
                    {grant.featureKey}
                  </ResourceLink>
                </TableCell>
                <TableCell>{grant.scopeType}</TableCell>
                <TableCell>{renderTarget(analysis)}</TableCell>
                <TableCell>
                  <Chip size="small" label={grant.state} />
                </TableCell>
                <TableCell>
                  {analysis.fallbackState
                    ? `${analysis.fallbackState} (${analysis.fallbackSource})`
                    : '—'}
                </TableCell>
                <TableCell>{analysis.reason}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const FeatureGrantCleanup: React.FC = () => {
  const apollo = useApolloClient();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const canWrite = useCanWrite();

  const [filter, setFilter] = React.useState<CleanupFilter | null>(null);
  const [data, setData] = React.useState<CleanupData | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  // Set by the link from a client's Feature Grants tab.
  const prefilledClientId = useSearchParams()[0].get('clientId') ?? undefined;

  // A slow earlier run would otherwise render its grants against the newer
  // filter's client. Only the latest run may write.
  const runId = React.useRef(0);
  const analyze = React.useCallback(
    async (next: CleanupFilter) => {
      const run = (runId.current += 1);
      setLoading(true);
      setError(null);
      setSelected(new Set());
      try {
        const loaded = await loadCleanupData(apollo, next);
        if (run === runId.current) {
          setData(loaded);
        }
      } catch (loadError) {
        if (run === runId.current) {
          setData(null);
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load feature grants.',
          );
        }
      } finally {
        if (run === runId.current) {
          setLoading(false);
        }
      }
    },
    [apollo],
  );

  // Keyed by client, not by `analyze` identity, which would restart it midway.
  const autoAnalyzed = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    if (prefilledClientId && autoAnalyzed.current !== prefilledClientId) {
      autoAnalyzed.current = prefilledClientId;
      const next = { clientId: prefilledClientId };
      setFilter(next);
      analyze(next);
    }
  }, [prefilledClientId, analyze]);

  const handleSubmit = async (values: Record<string, unknown>) => {
    const clientId = values.clientId as string | undefined;
    if (!clientId) {
      return;
    }
    const next: CleanupFilter = {
      clientId,
      featureId: (values.featureId as string | undefined) || undefined,
    };
    setFilter(next);
    await analyze(next);
  };

  const analyses = React.useMemo(() => {
    if (!data) {
      return [];
    }
    return analyzeFeatureGrants(data);
  }, [data]);

  const byVerdict = (verdict: CleanupVerdict) =>
    analyses.filter((analysis) => analysis.verdict === verdict);
  const redundant = byVerdict('redundant');
  const review = byVerdict('review');
  const keepCount = analyses.length - redundant.length - review.length;

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) {
        next.add(id);
      }
      return next;
    });

  // The unchanged-state guarantee covers redundant grants only, and a ticked
  // review grant can be what a redundant one falls back to — so the confirm
  // counts them rather than showing a flat total.
  const selectedReviewCount = review.filter((analysis) =>
    selected.has(analysis.grant.id),
  ).length;

  // Select-all only ever covers the redundant table — a "needs review" grant
  // has to be ticked deliberately.
  const toggleAllRedundant = (checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const analysis of redundant) {
        if (checked) {
          next.add(analysis.grant.id);
        } else {
          next.delete(analysis.grant.id);
        }
      }
      return next;
    });

  // Deleting a review grant can invalidate the redundant verdict of another
  // grant in the same batch — but only when one actually falls back to it.
  const cascadeRisk = redundant.some(
    (analysis) =>
      selected.has(analysis.grant.id) &&
      analysis.fallbackGrantId &&
      selected.has(analysis.fallbackGrantId),
  );

  const plural = selected.size === 1 ? '' : 's';
  const confirmTitle =
    selectedReviewCount === 0
      ? `Delete ${selected.size} redundant grant${plural}?`
      : `Delete ${selected.size} grant${plural}?`;
  const confirmContent =
    selectedReviewCount === 0
      ? `Each one repeats what a broader scope already says, so nothing changes for any member of ${data?.clientName ?? 'this client'}. This can't be undone here.`
      : [
          `${selectedReviewCount} need review — those may be deciding access for some members.`,
          `The other ${selected.size - selectedReviewCount} are redundant.`,
          cascadeRisk &&
            "Some of those redundant grants fall back to a review grant you're deleting too, so their verdicts don't hold if both go.",
          "This can't be undone here.",
        ]
          .filter(Boolean)
          .join(' ');

  const handleDelete = async () => {
    setDeleting(true);
    const ids = [...selected];
    const failed: string[] = [];
    // A select-all can be hundreds of grants, and the mutation takes one id.
    for (let i = 0; i < ids.length; i += DELETE_CONCURRENCY) {
      await Promise.all(
        ids.slice(i, i + DELETE_CONCURRENCY).map((id) =>
          dataProvider
            .delete('FeatureGrant', { id, previousData: { id } })
            .catch(() => {
              failed.push(id);
            }),
        ),
      );
    }
    setDeleting(false);
    setConfirmOpen(false);
    const deleted = ids.length - failed.length;
    if (failed.length > 0) {
      notify(
        `Deleted ${deleted} of ${ids.length} grants — ${failed.length} failed.`,
        { type: 'warning' },
      );
    } else {
      notify(`Deleted ${deleted} grant${deleted === 1 ? '' : 's'}.`, {
        type: 'success',
      });
    }
    if (filter) {
      // Verdicts derive from the whole set, so rebuild it rather than patch.
      await analyze(filter);
    }
  };

  return (
    <Box mt={2}>
      <Title title="Feature Grant Cleanup" />
      <Typography variant="h3">Feature Grant Cleanup</Typography>
      <Paper sx={{ mt: 2, p: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Finds grants a broader scope already covers — a member grant saying
          the same thing as their client&apos;s, a studio grant matching the
          client grant above it, a client grant matching the feature default.
          Deleting a redundant grant leaves every member&apos;s resolved state
          unchanged.
        </Typography>
        <ResourceContextProvider value="FeatureGrant">
          <Form
            onSubmit={handleSubmit}
            defaultValues={{ clientId: prefilledClientId ?? '' }}
          >
            {/* Wide enough that client and feature names don't ellipse. */}
            <Box
              display="flex"
              gap={2}
              alignItems="flex-start"
              flexWrap="wrap"
              sx={{
                '& .MuiFormControl-root': { width: 320, maxWidth: '100%' },
              }}
            >
              <AutocompleteClientInput
                source="clientId"
                label="Client"
                size="small"
                isRequired
              />
              <ReferenceInput source="featureId" reference="Feature">
                <AutocompleteInput
                  label="Feature (optional)"
                  optionText="key"
                  size="small"
                  helperText="Narrows the analysis to one feature."
                />
              </ReferenceInput>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                // Sits level with the inputs, above their helper text.
                sx={{ mt: 0.5 }}
              >
                Analyze
              </Button>
            </Box>
          </Form>
        </ResourceContextProvider>
      </Paper>

      {loading && (
        <Box display="flex" alignItems="center" gap={1} mt={2}>
          <CircularProgress size={20} />
          <Typography>Loading grants and their scope targets…</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && data && (
        <Box mt={3}>
          <Typography variant="body2" color="text.secondary">
            {analyses.length} of {data.totalGrants} grant
            {data.totalGrants === 1 ? '' : 's'} for {data.clientName} analyzed —{' '}
            {redundant.length} redundant, {review.length} need review,{' '}
            {keepCount} change the outcome.
          </Typography>

          {canWrite && (
            <Box mt={2}>
              <Button
                variant="contained"
                color="error"
                disabled={selected.size === 0}
                onClick={() => setConfirmOpen(true)}
              >
                Delete selected ({selected.size})
              </Button>
            </Box>
          )}

          <Box mt={3}>
            <Typography variant="h6" fontSize={16}>
              Redundant ({redundant.length})
            </Typography>
            {redundant.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing redundant for this filter.
              </Typography>
            ) : (
              <GrantTable
                analyses={redundant}
                selected={selected}
                onToggle={canWrite ? toggle : undefined}
                onToggleAll={toggleAllRedundant}
              />
            )}
          </Box>

          {review.length > 0 && (
            <Box mt={4}>
              <Typography variant="h6" fontSize={16}>
                Needs review ({review.length})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Not decidable from admin data alone — select these one at a time
                only if you know the grant is safe to drop.
              </Typography>
              <GrantTable
                analyses={review}
                selected={selected}
                onToggle={canWrite ? toggle : undefined}
              />
            </Box>
          )}
        </Box>
      )}

      <Confirm
        isOpen={confirmOpen}
        loading={deleting}
        title={confirmTitle}
        content={confirmContent}
        confirm={`Delete ${selected.size}`}
        confirmColor={selectedReviewCount === 0 ? 'primary' : 'warning'}
        onConfirm={handleDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </Box>
  );
};

export default FeatureGrantCleanup;
